import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import { Transition } from 'react-transition-group';
import GetStarted from './pages/GetStarted';
import Intro from './pages/Intro';
import Community from './pages/Community';
import Search from './pages/Search';
import Notes from './pages/Notes';
import Explore from './pages/Explore';
import { Logo } from '@repo/icons';
import styles from './WelcomeGuide.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import AppState from '@repo/redux/app-state';
import { toggleWelcomeGuideVisibility } from '@repo/redux/actions/ui';
import analytics from '@repo/utils/helpers/analytics';
import { useContactSearch, useAuthContext } from '@repo/hooks';
import { selectContactGetter } from '@repo/redux/actions/contacts';

const userName = (user) => {
  let name = '';

  if (user.first_name) {
    name = user.first_name;
  }

  if (name != '' && user.last_name) {
    name = `${name} ${user.last_name[0]}.`;
  }

  return name != '' ? name : 'you';
};

function Book({ user, dismiss, turnToPage, pageIdx }) {
  const getContact = useSelector(selectContactGetter);
  const [setSearchTerm, searchState] = useContactSearch({
    defaultTerm: 'closer',
    resultCount: 5,
  });
  const contactList = searchState.results.asContactArray(getContact);

  useEffect(() => {
    const fetchedTooFew = !searchState.results.isFetching && searchState.results.items.size < 5;

    if (fetchedTooFew && searchState.term == 'closer') {
      setSearchTerm('a');
      return;
    }

    if (fetchedTooFew && searchState.term == 'a') {
      setSearchTerm('close');
    }
  }, [searchState]);

  return (
    <div className={cn(styles.book, styles[`onPage${pageIdx}`])}>
      <Intro user={user} onClick={turnToPage(0)} turnToPage={turnToPage} />
      <Community user={user} onClick={turnToPage(1)} />
      <Notes onClick={turnToPage(2)} contacts={contactList} />
      <Search onClick={turnToPage(3)} />
      <Explore onClick={turnToPage(4)} />
      <GetStarted onClick={turnToPage(5)} dismiss={dismiss} />
    </div>
  );
}

function WelcomeGuideView({ dismiss, animationState }) {
  const { user } = useAuthContext();
  const [visiblePageIdx, setVisiblePageIdx] = useState(0);

  const turnToPage = (idx) => () => {
    if (visiblePageIdx != idx) {
      setVisiblePageIdx(idx);
    }
  };

  const advance = () => {
    if (visiblePageIdx < 5) {
      setVisiblePageIdx(visiblePageIdx + 1);
    }
  };

  const reverse = () => {
    if (visiblePageIdx > 0) {
      setVisiblePageIdx(visiblePageIdx - 1);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key == 'ArrowRight') {
      advance();
    } else if (e.key == 'ArrowLeft') {
      reverse();
    }
  };

  useEffect(() => {
    analytics.track('Welcome Guide Viewed');
  }, []);

  return (
    <div
      className={cn(styles.container, animationState, 'welcome-guide-container')}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className={styles.innerContainer}>
        <div className={cn(styles.orb, styles.orb1)} />
        <div className={cn(styles.orb, styles.orb2)} />
        <div className={cn(styles.orb, styles.orb3)} />
        <div className={cn(styles.orb, styles.orb4)} />
        <div className={styles.content}>
          <div className={styles.title}>
            <Logo customColor="rgb(245,245,245)" width="37" />
            <h4>A Brief Introduction</h4>
            <h5>Prepared for {userName(user)}</h5>
          </div>
          <Book user={user} dismiss={dismiss} turnToPage={turnToPage} pageIdx={visiblePageIdx} />
          <button className={styles.skipBtn} onClick={dismiss}>
            SKIP
          </button>
        </div>
      </div>
    </div>
  );
}

export default function WelcomeGuide() {
  const dispatch = useDispatch();
  const isVisible = useSelector((state: AppState) => state.ui.welcomeGuide.visible);
  const dismiss = () => dispatch(toggleWelcomeGuideVisibility(false));

  return (
    <Transition in={isVisible} timeout={1000}>
      {(animationState) => {
        return (
          animationState !== 'exited' && (
            <WelcomeGuideView animationState={animationState} dismiss={dismiss} />
          )
        );
      }}
    </Transition>
  );
}
